import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as cartApi from '../api/cartApi';

const getGuestCart = () => {
  try {
    return JSON.parse(localStorage.getItem('rrc_guest_cart') || '[]');
  } catch {
    return [];
  }
};

const saveGuestCart = (items) => {
  localStorage.setItem('rrc_guest_cart', JSON.stringify(items));
};

const calculateTotals = (items) => {
  const totalAmount = items.reduce((sum, item) => {
    const price = item.discountPrice || item.price;
    return sum + price * item.quantity;
  }, 0);
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  return { totalAmount, totalItems };
};

export const fetchCart = createAsyncThunk('cart/fetchCart', async (_, { getState, rejectWithValue }) => {
  try {
    const { auth } = getState();
    if (auth.isAuthenticated) {
      const data = await cartApi.fetchCart();
      return data;
    } else {
      const guestCart = getGuestCart();
      return { items: guestCart, ...calculateTotals(guestCart) };
    }
  } catch (err) {
    return rejectWithValue(err.message || 'Failed to fetch cart');
  }
});

export const addToCart = createAsyncThunk(
  'cart/addToCart',
  async (itemData, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (auth.isAuthenticated) {
        const data = await cartApi.addToCart(itemData);
        return data;
      } else {
        let guestCart = getGuestCart();
        const existingIndex = guestCart.findIndex(
          (item) => item.productId === itemData.productId && item.size === itemData.size && item.color === itemData.color
        );
        if (existingIndex > -1) {
          guestCart[existingIndex].quantity += itemData.quantity || 1;
        } else {
          guestCart.push({ ...itemData, _id: Date.now().toString(), quantity: itemData.quantity || 1 });
        }
        saveGuestCart(guestCart);
        return { items: guestCart, ...calculateTotals(guestCart) };
      }
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to add to cart');
    }
  }
);

export const updateCartItem = createAsyncThunk(
  'cart/updateCartItem',
  async ({ itemId, quantity }, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (auth.isAuthenticated) {
        const data = await cartApi.updateCartItem(itemId, { quantity });
        return data;
      } else {
        let guestCart = getGuestCart();
        guestCart = guestCart.map((item) =>
          item._id === itemId ? { ...item, quantity } : item
        );
        saveGuestCart(guestCart);
        return { items: guestCart, ...calculateTotals(guestCart) };
      }
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to update cart');
    }
  }
);

export const removeCartItem = createAsyncThunk(
  'cart/removeCartItem',
  async (itemId, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (auth.isAuthenticated) {
        await cartApi.removeCartItem(itemId);
        return itemId;
      } else {
        let guestCart = getGuestCart();
        guestCart = guestCart.filter((item) => item._id !== itemId);
        saveGuestCart(guestCart);
        return { items: guestCart, ...calculateTotals(guestCart) };
      }
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to remove item');
    }
  }
);

export const clearCart = createAsyncThunk('cart/clearCart', async (_, { getState, rejectWithValue }) => {
  try {
    const { auth } = getState();
    if (auth.isAuthenticated) {
      await cartApi.clearCart();
    }
    localStorage.removeItem('rrc_guest_cart');
    return { items: [], totalAmount: 0, totalItems: 0 };
  } catch (err) {
    return rejectWithValue(err.message || 'Failed to clear cart');
  }
});

// Login/register ke baad guest cart ko server cart me merge karo
export const mergeGuestCart = createAsyncThunk(
  'cart/mergeGuestCart',
  async (_, { getState, rejectWithValue }) => {
    try {
      const { auth } = getState();
      if (!auth.isAuthenticated) {
        return rejectWithValue('Not authenticated');
      }
      const guestItems = getGuestCart();
      if (guestItems.length > 0) {
        for (const item of guestItems) {
          try {
            await cartApi.addToCart(item);
          } catch {
            // failed item (stock over etc.) skip karo, baaki merge ho jayenge
          }
        }
        saveGuestCart([]);
      }
      const data = await cartApi.fetchCart();
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Failed to merge cart');
    }
  }
);

const cartSlice = createSlice({
  name: 'cart',
  initialState: {
    items: getGuestCart(),
    ...calculateTotals(getGuestCart()),
    loading: false,
  },
  reducers: {
    resetCart: (state) => {
      state.items = [];
      state.totalAmount = 0;
      state.totalItems = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.totalAmount = action.payload.totalAmount || calculateTotals(action.payload.items || []).totalAmount;
        state.totalItems = action.payload.totalItems || calculateTotals(action.payload.items || []).totalItems;
      })
      .addCase(fetchCart.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addToCart.pending, (state) => {
        state.loading = true;
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.totalAmount = action.payload.totalAmount || calculateTotals(action.payload.items || []).totalAmount;
        state.totalItems = action.payload.totalItems || calculateTotals(action.payload.items || []).totalItems;
      })
      .addCase(addToCart.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.items = action.payload.items || [];
        state.totalAmount = action.payload.totalAmount || calculateTotals(action.payload.items || []).totalAmount;
        state.totalItems = action.payload.totalItems || calculateTotals(action.payload.items || []).totalItems;
      })
      .addCase(removeCartItem.fulfilled, (state, action) => {
        if (action.payload.items) {
          state.items = action.payload.items;
          state.totalAmount = action.payload.totalAmount || 0;
          state.totalItems = action.payload.totalItems || 0;
        } else {
          state.items = state.items.filter((item) => item._id !== action.payload);
          const totals = calculateTotals(state.items);
          state.totalAmount = totals.totalAmount;
          state.totalItems = totals.totalItems;
        }
      })
      .addCase(clearCart.fulfilled, (state, action) => {
        state.items = action.payload.items;
        state.totalAmount = 0;
        state.totalItems = 0;
      })
      .addCase(mergeGuestCart.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || [];
        state.totalAmount = action.payload.totalAmount || calculateTotals(action.payload.items || []).totalAmount;
        state.totalItems = action.payload.totalItems || calculateTotals(action.payload.items || []).totalItems;
      });
  },
});

export const { resetCart } = cartSlice.actions;
export default cartSlice.reducer;
